import {useEffect, useState, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import _ from 'lodash';
import {switchNightMode} from '../actions/UserActions';

const COOKIE_NAME = 'nightMode';

function isDarkFromCookie() {
    return document.cookie.indexOf(COOKIE_NAME + '=1') > -1;
}

function setCookie(value) {
    const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = COOKIE_NAME + '=' + (value ? '1' : '0') + ';expires=' + expires + ';path=/';
}

/**
 * Single source of truth for the night-mode toggle. Used by every UI
 * surface (sidebar slider, form-page header button, auth pages) so the
 * behaviour stays identical regardless of where the customer flips it:
 *   - cookie + body class flip immediately for instant UX
 *   - backend persistence runs only for logged-in users, debounced so
 *     rapid clicks collapse into one POST
 *   - on user load, backend value wins if it differs from the cookie
 */
export default function useNightMode() {
    const {user} = useSelector(state => state.user);
    const dispatch = useDispatch();
    const [isDark, setIsDark] = useState(isDarkFromCookie);
    const persistTimerRef = useRef(null);

    useEffect(() => {
        const nightMode = user?.preferences?.night_mode;
        if (!_.isEmpty(user) && nightMode !== isDarkFromCookie()) {
            setCookie(nightMode);
            setIsDark(nightMode);
            document.body.classList.toggle('dark', nightMode);
        }
    }, [user]);

    const toggle = () => {
        const next = !isDark;
        setIsDark(next);
        setCookie(next);
        document.body.classList.toggle('dark', next);

        if (_.isEmpty(user)) return;

        if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
        persistTimerRef.current = setTimeout(() => {
            dispatch(switchNightMode(next));
        }, 300);
    };

    return {isDark, toggle};
}
